import { useState, useEffect, useRef } from "react";

// ─── PASSWORD GATE ────────────────────────────────────────────
// Change ACCESS_PASSWORD to whatever you want to share internally
const ACCESS_PASSWORD = "agenticate2025";

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = () => {
    if (input === ACCESS_PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setInput("");
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #0D4A26 0%, #1B6B3A 60%, #2D8A50 100%)",
      fontFamily: "'Inter', system-ui, sans-serif"
    }}>
      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .shake { animation: shake 0.4s ease; }
      `}</style>
      <div style={{
        background: "white", borderRadius: 16, padding: "48px 40px",
        width: 360, boxShadow: "0 24px 60px rgba(0,0,0,0.3)", textAlign: "center"
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "linear-gradient(135deg, #1B6B3A, #2D8A50)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 24, margin: "0 auto 20px"
        }}>🔒</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 6 }}>Agenticate</div>
        <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 32 }}>Internal access only. Enter your password to continue.</div>
        <div className={shake ? "shake" : ""}>
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false); }}
            onKeyDown={e => e.key === "Enter" && handleSubmit()}
            placeholder="Enter password"
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 8, fontSize: 14,
              border: `2px solid ${error ? "#FCA5A5" : "#E5E7EB"}`,
              outline: "none", boxSizing: "border-box", marginBottom: 8,
              background: error ? "#FEF2F2" : "white", color: "#111827",
              transition: "border-color 0.2s"
            }}
            autoFocus
          />
          {error && (
            <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 8 }}>
              Incorrect password. Please try again.
            </div>
          )}
          <button onClick={handleSubmit} style={{
            width: "100%", padding: "12px", borderRadius: 8, border: "none",
            background: "linear-gradient(135deg, #1B6B3A, #2D8A50)",
            color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(27,107,58,0.3)"
          }}>
            Access Demo →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MOCK DATA ────────────────────────────────────────────────
const CASES = {
  shell: {
    label: "Shell Structure — Suspicious",
    alert: { id: "ALT-20250320-0042", type: "E_TRANSFER_IN", amount: 89000, date: "2025-03-20", client: "2847391 Canada Inc.", clientType: "NUMBERED_COMPANY", channel: "ONLINE", priorAlerts: 0 },
    steps: [
      { agent: "Triage Agent", icon: "⚡", duration: 800, result: "STANDARD → upgraded to HIGH_PRIORITY after analysis", detail: "Nine e-transfers totalling CAD 89,000 over 30 days. Multiple transactions between CAD 8,500–9,900. Structuring pattern suspected.", flag: "HIGH" },
      { agent: "Registry Researcher", icon: "🔍", duration: 2200, result: "Entity incorporated 47 days before first transaction", detail: "Corporations Canada: 2847391 Canada Inc., incorporated 2025-02-01. Registered agent address — no physical presence. Director: nominee service provider. Annual return: not yet filed.", flag: "HIGH" },
      { agent: "UBO Analyst", icon: "🏗️", duration: 1800, result: "CRITICAL — Natural person UBO cannot be identified", detail: "Layer 1: 2847391 Canada Inc. → 100% owned by BVI Holdings Ltd (British Virgin Islands). Layer 2: BVI Holdings → 100% owned by Panama Foundation with undisclosed beneficiaries. Layer 3: Cannot identify natural person. Ownership chain exceeds 3 layers.", flag: "CRITICAL" },
      { agent: "Sanctions Screener", icon: "🛡️", duration: 1400, result: "CLEAR on all direct matches — linked entity flagged", detail: "Client: CLEAR on OFAC, OSFI, UN SC lists. Director (nominee firm): appears in FINTRAC typologies report as commonly used by structuring networks. PEP: Cannot confirm negative without UBO identity.", flag: "MEDIUM" },
      { agent: "Adverse Media Agent", icon: "📰", duration: 1600, result: "MATERIAL — ICIJ Offshore Leaks database match", detail: "BVI Holdings Ltd identified in ICIJ Offshore Leaks database. Source: credible international investigative journalism. Predicate: tax evasion / layering. Published 2023. Materiality test: PASS on all four criteria.", flag: "HIGH" },
      { agent: "Internal Systems", icon: "📊", duration: 1200, result: "CRITICAL — Structuring pattern confirmed", detail: "9 transactions over 30 days, each between $8,500–$9,900. Pattern consistent with deliberate structuring below the $10,000 LCTR threshold. No prior transaction history — no KYC profile to compare. No prior STRs.", flag: "CRITICAL" },
      { agent: "Adjudicator", icon: "⚖️", duration: 3000, result: "ESCALATE TO CASE INVESTIGATION", detail: "Memo MEMO-20250320-A7F2K1 generated. Confidence: 0.94. Reasonable grounds to suspect money laundering (layering stage). 5 material red flags. STR assessment required by senior analyst.", flag: "CRITICAL" },
    ],
    disposition: "ESCALATE_TO_CASE",
    confidence: 0.94,
    redFlags: [
      { id: "RF-001", cat: "TRANSACTION", desc: "Structuring pattern — 9 transactions between $8,500–$9,900", basis: "FINTRAC Guideline 2, Annex A — structuring is explicit indicator of suspicion", level: "CRITICAL" },
      { id: "RF-002", cat: "UBO", desc: "UBO chain exceeds 3 layers — natural person cannot be identified", basis: "FINTRAC Guideline B-8 s.3 — failure to identify UBO is itself a red flag", level: "CRITICAL" },
      { id: "RF-003", cat: "UBO", desc: "Entity incorporated 47 days before triggering transactions commence", basis: "FINTRAC Typologies — recently incorporated entities with immediate high-value activity", level: "HIGH" },
      { id: "RF-004", cat: "ADVERSE_MEDIA", desc: "BVI Holdings Ltd identified in ICIJ Offshore Leaks database", basis: "FINTRAC Guideline 2 — international investigative journalism is credible adverse media source", level: "HIGH" },
      { id: "RF-005", cat: "UBO", desc: "Registered agent address — no physical business presence", basis: "FINTRAC B-8 s.4 — nominee/registered agent addresses are red flags", level: "HIGH" },
    ],
    cleared: ["Client name: CLEAR on OFAC SDN, OSFI Consolidated, UN SC lists (searched 2025-03-20)", "No prior STRs on file for this client ID", "No direct criminal adverse media on named directors"],
    summary: "2847391 Canada Inc., a numbered Ontario company incorporated 47 days before activity commenced, received nine e-transfers totalling CAD 89,000 in a pattern consistent with deliberate structuring below the $10,000 threshold. The ultimate beneficial ownership cannot be traced to a natural person — a three-layer offshore structure terminates in a Panama Foundation with undisclosed beneficiaries. The corporate vehicle used in the ownership chain (BVI Holdings Ltd) appears in the ICIJ Offshore Leaks database. There are reasonable grounds to suspect money laundering at the layering stage. Escalation to case investigation and STR assessment is required.",
    time: "7m 42s",
    humanTime: "4h 15m"
  },
  legitimate: {
    label: "Commercial Landlord — Legitimate",
    alert: { id: "ALT-20250320-0051", type: "CASH_DEPOSIT", amount: 45000, date: "2025-03-20", client: "1234567 Ontario Inc.", clientType: "CORPORATION", channel: "BRANCH", priorAlerts: 0 },
    steps: [
      { agent: "Triage Agent", icon: "⚡", duration: 700, result: "STANDARD priority — routine cash deposit review", detail: "Cash deposit of CAD 45,000 from incorporated entity. LCTR filing required (separately). No prior alerts. Standard processing queue assigned.", flag: "LOW" },
      { agent: "Registry Researcher", icon: "🔍", duration: 1800, result: "Clean registry profile — 4-year-old active entity", detail: "Corporations Canada: 1234567 Ontario Inc., incorporated 2021-01-15. Active status. Annual return filed 2024. Registered address matches commercial property portfolio (not a registered agent). Business number validated.", flag: "LOW" },
      { agent: "UBO Analyst", icon: "🏗️", duration: 1200, result: "Single UBO — natural person identified, 100% ownership", detail: "Single UBO: individual (Ontario resident). 100% direct ownership confirmed. No layered structure. UBO DOB and address match CRM record. UBO complete: TRUE.", flag: "LOW" },
      { agent: "Sanctions Screener", icon: "🛡️", duration: 1100, result: "CLEAR — all lists, all names", detail: "Client and UBO screened against OFAC SDN, OSFI Consolidated, UN SC Consolidated, FINTRAC Domestic PEP list. All results: CLEAR. PEP status: NOT_PEP. Confidence: HIGH (exact name, DOB, and jurisdiction searched and matched).", flag: "LOW" },
      { agent: "Adverse Media Agent", icon: "📰", duration: 1300, result: "NON-MATERIAL — 3 results, none relating to this entity", detail: "3 adverse media results found for similar corporate names. Result 1: unrelated entity in BC (different BN). Result 2: 2016 civil lawsuit, different entity, resolved. Result 3: no financial crime content. Name disambiguation applied. All FAIL materiality test.", flag: "LOW" },
      { agent: "Internal Systems", icon: "📊", duration: 900, result: "CONSISTENT — activity matches documented KYC profile", detail: "90-day review: average monthly cash deposit $38,000–$46,000, consistent with prior 12 months. Client documented as commercial landlord. Cash rent collection from retail tenants is expected behaviour. No structuring. No prior STRs.", flag: "LOW" },
      { agent: "Adjudicator", icon: "⚖️", duration: 2100, result: "CLOSE ALERT — No financial crime concerns", detail: "Memo MEMO-20250320-B3C9M2 generated. Confidence: 0.97. Activity fully explained by KYC profile. All red flags investigated and cleared. LCTR filing confirmed separately.", flag: "LOW" },
    ],
    disposition: "CLOSE_ALERT",
    confidence: 0.97,
    redFlags: [],
    cleared: [
      "Cash deposit amount (CAD 45,000) is consistent with documented average monthly cash receipts of CAD 38,000–46,000",
      "Entity is a 4-year-old active corporation with current annual filing — not a purpose-built vehicle",
      "Single UBO (100% direct ownership) — no layered structure",
      "UBO is CLEAR on all sanctions and PEP lists",
      "3 adverse media results found — all confirmed as different entities via name disambiguation",
      "No structuring pattern — single large deposit, not multiple sub-threshold transactions",
    ],
    summary: "1234567 Ontario Inc., a 4-year-old active Ontario corporation, deposited CAD 45,000 in cash at a branch. The entity's UBO is a single Ontario-resident individual with no sanctions or PEP exposure. Registry records are current and consistent with a legitimate commercial landlord business. The deposit amount is consistent with documented average monthly cash rent collections from retail tenants. Three adverse media results were found for similar names — all confirmed as different entities via name disambiguation. No financial crime concerns identified. Alert recommended for close. LCTR filing has been confirmed as completed separately.",
    time: "8m 06s",
    humanTime: "3h 50m"
  }
};

const DISPOSITION_CONFIG = {
  ESCALATE_TO_CASE: { color: "#DC2626", bg: "#FEF2F2", label: "ESCALATE TO CASE", icon: "🚨" },
  CLOSE_ALERT: { color: "#059669", bg: "#ECFDF5", label: "CLOSE ALERT", icon: "✅" },
  FILE_STR: { color: "#7C3AED", bg: "#F5F3FF", label: "FILE STR", icon: "📋" },
  FURTHER_INFO_REQUIRED: { color: "#D97706", bg: "#FFFBEB", label: "FURTHER INFO REQUIRED", icon: "❓" },
  BLOCK_TRANSACTION: { color: "#DC2626", bg: "#FEF2F2", label: "BLOCK TRANSACTION", icon: "🛑" },
};

const FLAG_COLORS = {
  CRITICAL: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
  HIGH:     { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A" },
  MEDIUM:   { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
  LOW:      { bg: "#F0FDF4", text: "#059669", border: "#A7F3D0" },
};

// ─── TYPING EFFECT HOOK ───────────────────────────────────────

// ─── AGENT STEP COMPONENT ─────────────────────────────────────
function AgentStep({ step, index, isActive, isDone, delay }) {
  const [show, setShow] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (!isActive) return;
    const t1 = setTimeout(() => { setShow(true); setThinking(true); }, delay);
    const t2 = setTimeout(() => setThinking(false), delay + step.duration);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isActive, delay, step.duration]);

  useEffect(() => {
    if (!thinking) return;
    const id = setInterval(() => setDots(d => d.length >= 3 ? "" : d + "."), 400);
    return () => clearInterval(id);
  }, [thinking]);

  if (!show && !isDone) return null;

  const fc = FLAG_COLORS[step.flag] || FLAG_COLORS.LOW;

  return (
    <div style={{
      display: "flex", gap: 16, opacity: (show || isDone) ? 1 : 0,
      transition: "opacity 0.4s", marginBottom: 16
    }}>
      {/* Timeline */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 40 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 16, flexShrink: 0,
          background: thinking ? "#FEF3C7" : isDone || (show && !thinking) ? fc.bg : "#F3F4F6",
          border: `2px solid ${thinking ? "#F59E0B" : isDone || (show && !thinking) ? fc.border : "#E5E7EB"}`,
          transition: "all 0.3s"
        }}>
          {thinking ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⚙️</span> : step.icon}
        </div>
        {index < 6 && <div style={{ width: 2, flex: 1, minHeight: 20, background: "#E5E7EB", marginTop: 4 }} />}
      </div>

      {/* Content */}
      <div style={{
        flex: 1, background: "white", borderRadius: 10, padding: "14px 16px",
        border: `1px solid ${thinking ? "#FDE68A" : fc.border}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "border-color 0.3s", marginBottom: 4
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", fontFamily: "monospace" }}>
            {step.agent}
          </div>
          {thinking ? (
            <span style={{ fontSize: 11, color: "#F59E0B", fontWeight: 600 }}>PROCESSING{dots}</span>
          ) : (show || isDone) && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: fc.text,
              background: fc.bg, padding: "2px 8px", borderRadius: 999
            }}>{step.flag}</span>
          )}
        </div>
        {!thinking && (show || isDone) && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 5 }}>{step.result}</div>
            <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>{step.detail}</div>
          </>
        )}
        {thinking && (
          <div style={{ fontSize: 12, color: "#92400E", fontStyle: "italic" }}>
            Agent reasoning in progress{dots}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN DEMO COMPONENT ──────────────────────────────────────
function AgenticateDemoInner() {
  const [selectedCase, setSelectedCase] = useState("shell");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [elapsed, setElapsed] = useState(0);
  const [showMemo, setShowMemo] = useState(false);
  const [activeTab, setActiveTab] = useState("workflow");
  const timerRef = useRef(null);
  const caseData = CASES[selectedCase];

  const totalDuration = caseData.steps.reduce((a, s) => a + s.duration, 0) + 500;

  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => setElapsed(e => e + 100), 100);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const handleRun = () => {
    setRunning(true);
    setDone(false);
    setActiveStep(0);
    setElapsed(0);
    setShowMemo(false);

    let delay = 0;
    caseData.steps.forEach((step, i) => {
      delay += (i === 0 ? 200 : caseData.steps[i - 1].duration + 200);
      setTimeout(() => setActiveStep(i), delay);
    });

    setTimeout(() => {
      setRunning(false);
      setDone(true);
      setShowMemo(true);
      clearInterval(timerRef.current);
    }, totalDuration + 800);
  };

  const handleReset = () => {
    setRunning(false);
    setDone(false);
    setActiveStep(-1);
    setElapsed(0);
    setShowMemo(false);
    clearInterval(timerRef.current);
  };

  const disp = DISPOSITION_CONFIG[caseData.disposition];

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#F8FAFC", minHeight: "100vh" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
        .fade-in { animation: fadeIn 0.4s ease; }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div style={{
        background: "linear-gradient(135deg, #0D4A26 0%, #1B6B3A 60%, #2D8A50 100%)",
        padding: "20px 32px", color: "white"
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}>CONFIDENTIAL PoC</div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.5 }}>
                Agenticate
                <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 12, color: "rgba(255,255,255,0.7)" }}>
                  AML/KYC Agentic Squad — Interactive Demo
                </span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>Human-Centred Decision Intelligence</div>
              <div style={{ display: "flex", gap: 16 }}>
                {[["$200bn+", "Global FC Market"], ["90-95%", "False Positive Rate"], ["<10 min", "Target Processing"]].map(([val, lab]) => (
                  <div key={lab} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#86EFAC" }}>{val}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{lab}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 32px" }}>

        {/* ── CASE SELECTOR ──────────────────────────────────── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", marginBottom: 10, letterSpacing: 1 }}>SELECT TEST CASE</div>
          <div style={{ display: "flex", gap: 12 }}>
            {Object.entries(CASES).map(([key, c]) => (
              <button key={key} onClick={() => { setSelectedCase(key); handleReset(); }}
                style={{
                  padding: "12px 20px", borderRadius: 10, border: "2px solid",
                  borderColor: selectedCase === key ? "#1B6B3A" : "#E5E7EB",
                  background: selectedCase === key ? "#F0FDF4" : "white",
                  cursor: "pointer", transition: "all 0.2s", textAlign: "left"
                }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: selectedCase === key ? "#1B6B3A" : "#374151" }}>
                  {c.label}
                </div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                  {c.alert.client} · CAD {c.alert.amount.toLocaleString()} · {c.alert.type.replace(/_/g, " ")}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── MAIN LAYOUT ────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 420px", gap: 20, alignItems: "start" }}>

          {/* LEFT: Alert + Workflow */}
          <div>
            {/* Alert Card */}
            <div style={{
              background: "white", borderRadius: 12, border: "1px solid #E5E7EB",
              padding: 20, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>🚨 ALERT DETAILS</div>
                <div style={{
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
                  background: "#FEF3C7", color: "#92400E"
                }}>PENDING REVIEW</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {[
                  ["Alert ID", caseData.alert.id],
                  ["Alert Type", caseData.alert.type.replace(/_/g, " ")],
                  ["Amount", `CAD ${caseData.alert.amount.toLocaleString()}`],
                  ["Date", caseData.alert.date],
                  ["Client", caseData.alert.client],
                  ["Channel", caseData.alert.channel],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 600, marginBottom: 3, letterSpacing: 0.5 }}>{k.toUpperCase()}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "#F3F4F6", borderRadius: 8, padding: 4 }}>
              {[["workflow", "🤖 Agent Workflow"], ["audit", "📋 Audit Trail"]].map(([tab, label]) => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                  flex: 1, padding: "8px 12px", borderRadius: 6, border: "none",
                  background: activeTab === tab ? "white" : "transparent",
                  fontWeight: activeTab === tab ? 700 : 400, fontSize: 13,
                  color: activeTab === tab ? "#111827" : "#6B7280",
                  cursor: "pointer", boxShadow: activeTab === tab ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.2s"
                }}>{label}</button>
              ))}
            </div>

            {/* Workflow Steps */}
            {activeTab === "workflow" && (
              <div>
                {/* Timer bar */}
                {running && (
                  <div style={{ marginBottom: 16, background: "white", borderRadius: 10, padding: "12px 16px", border: "1px solid #FDE68A" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#92400E" }}>⚙️ Agentic workflow running...</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1B6B3A", fontFamily: "monospace" }}>
                        {(elapsed / 1000).toFixed(1)}s elapsed
                      </span>
                    </div>
                    <div style={{ background: "#F3F4F6", borderRadius: 999, height: 4 }}>
                      <div style={{
                        background: "linear-gradient(90deg, #1B6B3A, #86EFAC)",
                        height: "100%", borderRadius: 999,
                        width: `${Math.min(100, (elapsed / totalDuration) * 100)}%`,
                        transition: "width 0.1s"
                      }} />
                    </div>
                  </div>
                )}

                {activeStep === -1 && !done && (
                  <div style={{
                    textAlign: "center", padding: 40, background: "white",
                    borderRadius: 12, border: "2px dashed #E5E7EB", color: "#9CA3AF"
                  }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>🤖</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Ready to process alert</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Click "Run Agenticate" to start the 7-agent workflow</div>
                  </div>
                )}

                {caseData.steps.map((step, i) => {
                  let delay = 200;
                  for (let j = 0; j < i; j++) delay += caseData.steps[j].duration + 200;
                  return (
                    <AgentStep key={i} step={step} index={i}
                      isActive={activeStep >= 0}
                      isDone={done}
                      delay={delay}
                    />
                  );
                })}
              </div>
            )}

            {/* Audit Trail Tab */}
            {activeTab === "audit" && (
              <div style={{ background: "white", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", background: "#1B6B3A", color: "white" }}>
                  <div style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>IMMUTABLE AUDIT LOG — {caseData.alert.id}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>Every agent action is timestamped and logged for regulators (FINTRAC/OSFI)</div>
                </div>
                <div style={{ maxHeight: 420, overflowY: "auto" }}>
                  {done ? caseData.steps.map((step, i) => (
                    <div key={i} style={{
                      padding: "12px 16px", borderBottom: "1px solid #F3F4F6",
                      fontFamily: "monospace"
                    }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: "#6B7280" }}>
                          {new Date(Date.now() - (caseData.steps.length - i) * 60000).toISOString().replace("T", " ").slice(0, 19)} UTC
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#1B6B3A" }}>{step.agent.toUpperCase().replace(/ /g, "_")}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#374151" }}>{step.result}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                        Source: {step.agent === "Registry Researcher" ? "Corporations Canada API" : step.agent === "Sanctions Screener" ? "OFAC/OSFI/UN lists" : step.agent === "Adverse Media Agent" ? "Tavily web search" : "Internal system"} | Escalation: {step.flag}
                      </div>
                    </div>
                  )) : (
                    <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                      Run the workflow to generate the audit log
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Controls + Memo */}
          <div>
            {/* Run Controls */}
            <div style={{
              background: "white", borderRadius: 12, border: "1px solid #E5E7EB",
              padding: 20, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14 }}>⚡ DEMO CONTROLS</div>
              <button onClick={running ? undefined : done ? handleReset : handleRun}
                style={{
                  width: "100%", padding: "14px 20px", borderRadius: 10, border: "none",
                  background: running ? "#F3F4F6" : done ? "#0D4A26" : "linear-gradient(135deg, #1B6B3A, #2D8A50)",
                  color: running ? "#9CA3AF" : "white", fontSize: 14, fontWeight: 700,
                  cursor: running ? "not-allowed" : "pointer", marginBottom: 10,
                  boxShadow: running || done ? "none" : "0 4px 12px rgba(27,107,58,0.3)",
                  transition: "all 0.2s"
                }}>
                {running ? "⚙️  Processing..." : done ? "🔄  Reset Demo" : "▶  Run Agenticate"}
              </button>

              {/* Side-by-side comparison */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{
                  background: "#FEF2F2", borderRadius: 8, padding: "10px 12px",
                  border: "1px solid #FECACA", opacity: done ? 1 : 0.4
                }}>
                  <div style={{ fontSize: 10, color: "#DC2626", fontWeight: 700, marginBottom: 4 }}>👤 HUMAN ANALYST</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626" }}>{caseData.humanTime}</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF" }}>avg processing time</div>
                </div>
                <div style={{
                  background: "#F0FDF4", borderRadius: 8, padding: "10px 12px",
                  border: "1px solid #A7F3D0", opacity: done ? 1 : 0.4
                }}>
                  <div style={{ fontSize: 10, color: "#059669", fontWeight: 700, marginBottom: 4 }}>🤖 AGENTICATE</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>{done ? caseData.time : "--"}</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF" }}>actual processing time</div>
                </div>
              </div>
            </div>

            {/* Disposition Memo */}
            {showMemo && (
              <div className="fade-in" style={{
                background: "white", borderRadius: 12,
                border: `2px solid ${disp.color}`, overflow: "hidden",
                boxShadow: `0 4px 20px ${disp.color}20`
              }}>
                {/* Memo header */}
                <div style={{ background: disp.color, padding: "16px 20px", color: "white" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 20 }}>{disp.icon}</span>
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{disp.label}</span>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.85 }}>
                    MEMO-{caseData.alert.date.replace(/-/g, "")}-A7F2K1 · 
                    Confidence: {(caseData.confidence * 100).toFixed(0)}% · 
                    Human review required
                  </div>
                </div>

                <div style={{ padding: 20, maxHeight: 520, overflowY: "auto" }}>
                  {/* Executive summary */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", marginBottom: 8, letterSpacing: 0.5 }}>EXECUTIVE SUMMARY</div>
                    <div style={{
                      fontSize: 13, lineHeight: 1.7, color: "#374151",
                      background: "#F9FAFB", borderRadius: 8, padding: 14,
                      borderLeft: `3px solid ${disp.color}`
                    }}>{caseData.summary}</div>
                  </div>

                  {/* Red flags */}
                  {caseData.redFlags.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", marginBottom: 8, letterSpacing: 0.5 }}>
                        RED FLAGS IDENTIFIED ({caseData.redFlags.length})
                      </div>
                      {caseData.redFlags.map(rf => {
                        const fc = FLAG_COLORS[rf.level];
                        return (
                          <div key={rf.id} style={{
                            marginBottom: 8, padding: "10px 12px", borderRadius: 8,
                            background: fc.bg, border: `1px solid ${fc.border}`
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: fc.text }}>{rf.id} · {rf.cat}</span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: fc.text,
                                background: "white", padding: "1px 6px", borderRadius: 999 }}>{rf.level}</span>
                            </div>
                            <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, marginBottom: 3 }}>{rf.desc}</div>
                            <div style={{ fontSize: 11, color: "#6B7280", fontStyle: "italic" }}>{rf.basis}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Cleared concerns */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", marginBottom: 8, letterSpacing: 0.5 }}>
                      CONCERNS INVESTIGATED & CLEARED ({caseData.cleared.length})
                    </div>
                    {caseData.cleared.map((c, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 12, color: "#374151" }}>
                        <span style={{ color: "#059669", flexShrink: 0, marginTop: 1 }}>✓</span>
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>

                  {/* Sources */}
                  <div style={{
                    padding: "10px 12px", background: "#F0FDF4", borderRadius: 8,
                    border: "1px solid #A7F3D0", fontSize: 11, color: "#059669", fontWeight: 600
                  }}>
                    🔗 All findings are sourced and cited in the full audit trail.
                    Every agent decision is logged with source URL, timestamp, and reasoning chain.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── BOTTOM STATS BAR ────────────────────────────────── */}
        <div style={{
          marginTop: 28, padding: "16px 24px", background: "white",
          borderRadius: 12, border: "1px solid #E5E7EB",
          display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
        }}>
          {[
            ["7 Specialist Agents", "Orchestrated by LangGraph"],
            ["FINTRAC + OSFI", "Regulatory framework embedded"],
            ["100% Cited", "Every decision sourced"],
            ["Maker-Checker", "Human always approves"],
            ["Audit-Ready", "Immutable log for regulators"],
          ].map(([val, lab]) => (
            <div key={lab} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#1B6B3A" }}>{val}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{lab}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PASSWORD-GATED EXPORT ────────────────────────────────────
export default function AgenticateDemo() {
  const [unlocked, setUnlocked] = useState(false);

  // Remember unlock in session — refreshing the page won't kick you out
  useEffect(() => {
    if (sessionStorage.getItem("agenticate_unlocked") === "true") {
      setUnlocked(true);
    }
  }, []);

  const handleUnlock = () => {
    sessionStorage.setItem("agenticate_unlocked", "true");
    setUnlocked(true);
  };

  if (!unlocked) return <PasswordGate onUnlock={handleUnlock} />;
  return <AgenticateDemoInner />;
}

CASES.fraud_app = {
  label: "APP Fraud — Bank Impersonation",
  domain: "FRAUD",
  alert: { id: "FRD-20250320-0091", type: "OUTBOUND_WIRE", amount: 47500, date: "2025-03-20", client: "Margaret Thornton", clientType: "INDIVIDUAL", channel: "TELEPHONE_BANKING", priorAlerts: 0 },
  steps: [
    { agent: "Triage Agent", icon: "⚡", duration: 600, result: "CRITICAL — APP Fraud, Impersonation. Real-time block recommended.", detail: "Outbound wire CAD $47,500 to new payee via telephone banking. Pre-transaction call centre contact 38 minutes prior. CRITICAL urgency.", flag: "CRITICAL" },
    { agent: "Velocity & Transaction Agent", icon: "💸", duration: 900, result: "1,840% above customer baseline — extreme anomaly", detail: "Amount: CAD $47,500 vs 90-day average of $2,580. First-time wire payee. Zero prior wire transfer history.", flag: "CRITICAL" },
    { agent: "Behavioural Analytics Agent", icon: "📈", duration: 1100, result: "CRITICAL deviation — no prior behaviour resembles this", detail: "36-month history reviewed. No prior wires, no large outbound. This transaction has zero precedent.", flag: "CRITICAL" },
    { agent: "Device & Channel Agent", icon: "📱", duration: 800, result: "Call centre contact 38 mins prior — PRIMARY APP indicator", detail: "Call centre log: customer called at 1:36pm. RBC Fraud Prevention told her account was compromised. Asked to move money to safe account.", flag: "CRITICAL" },
    { agent: "Social Engineering Agent", icon: "🎭", duration: 1300, result: "CRITICAL — CAFC bank impersonation script confirmed", detail: "CAFC ScamTracker: exact match to bank investigator scam typology. $11.7M in Canadian losses in 2025. Customer age 71 — elevated vulnerability.", flag: "CRITICAL" },
    { agent: "Payee Intelligence Agent", icon: "🏦", duration: 1000, result: "CRITICAL — Payee account 12 days old, CoP FAIL", detail: "Payee account opened 12 days ago. Zero prior transactions. RBC Security Holdings Ltd is not a registered entity. Confirmation of Payee: FAIL.", flag: "CRITICAL" },
    { agent: "Adjudicator", icon: "⚖️", duration: 2200, result: "BLOCK TRANSACTION — 6 CRITICAL indicators", detail: "Confidence: 0.97. Six CRITICAL indicators confirm APP impersonation fraud. Branch staff to contact customer via known number only. CAFC report to be filed.", flag: "CRITICAL" },
  ],
  disposition: "BLOCK_TRANSACTION",
  confidence: 0.97,
  redFlags: [
    { id: "RF-001", cat: "SOCIAL ENG.", desc: "Pre-transaction call centre contact — customer directed to transfer to safe account", basis: "CAFC bank investigator scam — $11.7M losses in 2025", level: "CRITICAL" },
    { id: "RF-002", cat: "TRANSACTION", desc: "Amount $47,500 — 1,840% above 90-day baseline", basis: "Velocity anomaly — extreme outlier with no prior precedent", level: "CRITICAL" },
    { id: "RF-003", cat: "PAYEE", desc: "Payee account opened 12 days ago — zero transaction history", basis: "New account receiving first-time high-value transfer — mule account indicator", level: "CRITICAL" },
    { id: "RF-004", cat: "PAYEE", desc: "Confirmation of Payee FAIL — RBC Security Holdings Ltd not a registered entity", basis: "Name mismatch confirms fraudulent payee identity", level: "CRITICAL" },
    { id: "RF-005", cat: "VULNERABILITY", desc: "Customer age 71 — elevated vulnerability. First large wire ever.", basis: "FCAC elder financial abuse obligations apply", level: "CRITICAL" },
    { id: "RF-006", cat: "BEHAVIOURAL", desc: "Zero precedent in 36-month history", basis: "Extreme deviation from established customer profile", level: "CRITICAL" },
  ],
  cleared: [
    "No prior fraud history on this customer account",
    "No account takeover indicators — customer herself initiated via telephone banking",
    "Transaction amount is within customer available balance",
  ],
  summary: "Margaret Thornton, age 71, attempted to authorise a CAD $47,500 wire to a payee account opened 12 days ago after receiving a call from someone impersonating RBC fraud prevention. Six converging CRITICAL indicators confirm APP impersonation fraud matching the CAFC bank investigator scam typology responsible for $11.7M in Canadian losses in 2025. Transaction must be blocked. Customer to be contacted by branch staff only.",
  time: "6m 54s",
  humanTime: "2h 30m",
  blockRecommended: true,
};
